import { useEffect, useMemo, useState } from 'react';
import { Fade } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import useStyles from './styles';
import { AddButton, VerticalLine, EditableBloc, IconTitle } from '../../../../components';
import NoContent from './components/NoContent';
import SectionsPanel from './components/SectionPanel';
import { Section } from '../../../../entities';
import FlexContainer from '../../../../components/FlexContainer';
import { useFetch, useSections } from '../../../../hooks';

export const Sections = (): JSX.Element => {
  const { id: condominiumId } = useParams<{ id: string }>();
  const styles = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const [sections, setSections] = useState<Section[]>([]);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [selectedSection, setSelectedSection] = useState<Section>({} as Section);
  const repository = useSections(condominiumId!);
  const { data, loading, refetch } = useFetch(repository.fetchAll);

  useEffect(() => {
    setIsPanelVisible(false);
    setSections(data ?? []);
  }, [condominiumId, data]);

  const handleSetPanelOpen = () => {
    setSelectedSection({} as Section);
    setIsPanelVisible(!isPanelVisible);
  };

  const handleSelectSection = (section?: Section) => {
    if (section) {
      setSelectedSection(section);
    }
    setIsPanelVisible(true);
  };

  const handleCreate = async (value: Section) => {
    await repository.create(value);
  };

  const handleUpdate = async (value: Section) => {
    await repository.update(value);
  };

  const handleSubmit = async (value: Section) => {
    try {
      if (value.id) {
        await handleUpdate(value);
      } else {
        await handleCreate(value);
      }

      enqueueSnackbar('O bloco foi salvo com sucesso!', { variant: 'success' });
      refetch();
    } catch (err) {
      enqueueSnackbar('Não foi possível salvar o bloco, tente novamente mais tarde', { variant: 'error' });
    }
  };

  const showNoContent = useMemo(() => !isPanelVisible && sections.length === 0, [isPanelVisible, sections]);

  if (loading && sections.length === 0) {
    return <></>;
  }

  return (
    <div className={styles.container}>
      {showNoContent ? (
        <NoContent />
      ) : (
        <div className={styles.containerRight}>
          <IconTitle title="Lista de blocos" />
          <FlexContainer>
            {sections.map((section) => (
              <EditableBloc<Section>
                label={section.label}
                value={section}
                key={section.id}
                onClickEdit={handleSelectSection}
              />
            ))}
          </FlexContainer>
        </div>
      )}
      <Fade in={isPanelVisible} mountOnEnter unmountOnExit>
        <VerticalLine />
      </Fade>
      <SectionsPanel
        visible={isPanelVisible}
        section={selectedSection}
        onClickClose={handleSetPanelOpen}
        onSubmit={handleSubmit}
      />
      {!isPanelVisible && <AddButton label="adicionar bloco" onClick={() => handleSetPanelOpen()} />}
    </div>
  );
};

export default Sections;
